import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { generateYearOptions } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-filters/goals-matrix-filters';
import type { YearOption } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-filters/goals-matrix-filters';
import {
  DEFAULT_VIEW,
  YEAR_OPTIONS_FUTURE,
  YEAR_OPTIONS_PAST,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.constants';
import {
  buildTabPath,
  getExplicitViewFromSearch,
  getTabCounts,
  shouldLandOnEvaluating,
  getYearFromPath,
  groupByTab,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.helpers';
import type {
  CaptureCounts,
  CriticalPopulationsView,
  DecisionActionKey,
  DeleteTarget,
  DrawerState,
  GroupedCapture,
  OpenDecisionPanel,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.types';

import type { CriticalPopulationResponse } from 'api/generated/schemas';

import { useCriticalPopulations } from 'hooks/api/use-critical-populations';

import { getCurrentYear } from 'helpers/date.helpers';

interface UseCriticalPopulationsPageResult {
  enteringClassYear: number;
  yearOptions: YearOption[];
  handleYearChange: (year: number) => void;
  view: CriticalPopulationsView;
  handleViewChange: (view: CriticalPopulationsView) => void;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  grouped: GroupedCapture;
  counts: CaptureCounts | undefined;
  drawer: DrawerState;
  drawerRow: CriticalPopulationResponse | undefined;
  openEdit: (id: number) => void;
  openCreate: () => void;
  closeDrawer: () => void;
  openPanel: OpenDecisionPanel | null;
  openDecision: (id: number, action: DecisionActionKey) => void;
  closeDecision: () => void;
  deleteTarget: DeleteTarget | null;
  openDelete: (target: DeleteTarget) => void;
  closeDelete: () => void;
}

const CLOSED_DRAWER: DrawerState = { mode: 'closed' };

export const useCriticalPopulationsPage = (
  clientId: string | number
): UseCriticalPopulationsPageResult => {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawer, setDrawer] = useState<DrawerState>(CLOSED_DRAWER);
  const [openPanel, setOpenPanel] = useState<OpenDecisionPanel | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const yearFromPath = getYearFromPath(location.pathname);
  const enteringClassYear = yearFromPath ?? getCurrentYear();
  const explicitView = getExplicitViewFromSearch(location.search ?? '');
  const view = explicitView ?? DEFAULT_VIEW;
  const landedYear = useRef<number | null>(null);

  useEffect(() => {
    if (yearFromPath === null) {
      navigate(buildTabPath(clientId, enteringClassYear, view), {
        replace: true,
      });
    }
  }, [yearFromPath, clientId, enteringClassYear, view, navigate]);

  const { data, isPending, isError, refetch } = useCriticalPopulations({
    clientId: Number(clientId),
    enteringClassYear,
  });

  const yearOptions = useMemo(
    () =>
      generateYearOptions({
        futureYears: YEAR_OPTIONS_FUTURE,
        pastYears: YEAR_OPTIONS_PAST,
      }),
    []
  );

  const grouped = useMemo(() => groupByTab(data), [data]);
  const counts = isPending ? undefined : getTabCounts(grouped);

  useEffect(() => {
    if (!counts || yearFromPath === null || landedYear.current === yearFromPath)
      return;

    landedYear.current = yearFromPath;

    if (shouldLandOnEvaluating(explicitView, counts)) {
      navigate(buildTabPath(clientId, yearFromPath, 'evaluating'), {
        replace: true,
      });
    }
  }, [counts, yearFromPath, explicitView, clientId, navigate]);

  const drawerId = 'id' in drawer ? drawer.id : undefined;
  const drawerRow = useMemo(
    () => data?.find((row) => row.id === drawerId),
    [data, drawerId]
  );

  const closeDrawer = (): void => {
    setDrawer(CLOSED_DRAWER);
    setOpenPanel(null);
  };

  const openDrawer = (next: DrawerState): void => {
    setOpenPanel(null);
    setDrawer(next);
  };

  return {
    enteringClassYear,
    yearOptions,
    handleYearChange: (year) => {
      setOpenPanel(null);
      navigate(buildTabPath(clientId, year, view), { replace: true });
    },
    view,
    handleViewChange: (nextView) => {
      setOpenPanel(null);
      navigate(buildTabPath(clientId, enteringClassYear, nextView, true), {
        replace: true,
      });
    },
    isLoading: isPending,
    isError,
    refetch: () => {
      refetch();
    },
    grouped,
    counts,
    drawer,
    drawerRow,
    openEdit: (id) => openDrawer({ mode: 'edit', id }),
    openCreate: () => openDrawer({ mode: 'create' }),
    closeDrawer,
    openPanel,
    openDecision: (id, action) => setOpenPanel({ id, action }),
    closeDecision: () => setOpenPanel(null),
    deleteTarget,
    openDelete: setDeleteTarget,
    closeDelete: () => setDeleteTarget(null),
  };
};
